import React from "react";

export const fairLaunchContent = {
  create_fair_launch: {
    title: "Create a Fair Launch",
    content: (
      <>
        <h3>How to Create a Fair Launch on TrendPad</h3>
        <h4>Overview</h4>
        <p>
          A Fair Launch ensures that all participants receive the same token
          price, calculated at the end of the sale (Total Raised / Total Tokens
          Sold). This eliminates early-buyer advantages.
        </p>

        <h4>Step 1: Verify Token</h4>
        <ol>
          <li>
            Connect your wallet to the correct network (e.g., Optimism, BSC).
          </li>
          <li>
            Navigate to <strong>Launchpads</strong> &gt;{" "}
            <strong>Create fair launch</strong>.
          </li>
          <li>
            Input your <strong>Token Address</strong>. (Create one if needed).
          </li>
          <li>Select fundraising currency (e.g., ETH, BNB) and fee option.</li>
          <li>
            <strong>Choose Listing Option:</strong>
            <ul>
              <li>
                <strong>Auto Listing:</strong> Recommended. TrendPad
                automatically lists and locks liquidity.
              </li>
              <li>
                <strong>Manual Listing:</strong> You will need to add liquidity
                to the decentralized exchange (DEX) manually yourself.
              </li>
            </ul>
          </li>
          <li>
            Click <strong>Next</strong>
          </li>
        </ol>
        <img
          src="/docs/fairlaunch/1.png"
          alt="Fair Launch Step 1 - Verify Token"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <h4>Step 2: Fair Launch Information</h4>
        <ul>
          <li>
            <strong>Total Selling Amount:</strong> Tokens allocated for sale.
          </li>
          <li>
            <strong>Whitelist:</strong> Enable to restrict access to specific
            addresses.
          </li>
          <li>
            <strong>Soft Cap:</strong> Minimum funds required for success.
          </li>
          <li>
            <strong>Router:</strong> DEX where liquidity will be added.
          </li>
          <li>
            <strong>Liquidity (%):</strong> % of funds raised used for liquidity
            (51% - 100%).
          </li>
          <li>
            <strong>Enable Buyback:</strong> Optional. If enabled, (Liquidity %
            + Buyback %) must be &ge; 51%.
          </li>
          <li>
            <strong>Start/End Time:</strong> Duration of the pool.
          </li>
          <li>
            <strong>Liquidity Lockup:</strong> Duration to lock liquidity (e.g.,
            365 days).
          </li>
        </ul>
        <img
          src="/docs/fairlaunch/2.png"
          alt="Fair Launch Step 2 - Information"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <h4>Step 3: Additional Info</h4>
        <ul>
          <li>
            <strong>Logo & Website:</strong> Required. Logo url must be a direct
            image link (png/jpg/gif).
          </li>
          <li>
            <strong>Socials:</strong> Add links for Twitter, Telegram, etc.
          </li>
        </ul>
        <img
          src="/docs/fairlaunch/4.png"
          alt="Fair Launch Step 3 - Additional Info"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <h4>Step 4: Finish</h4>
        <ol>
          <li>Review all details.</li>
          <li>
            Click <strong>Approve Spending Token</strong> and confirm
            transaction.
          </li>
          <li>
            Click <strong>Confirm & Create Launchpad</strong>.
          </li>
        </ol>
        <img
          src="/docs/fairlaunch/5.png"
          alt="Fair Launch Step 4 - Finish"
          style={{ maxWidth: "100%", marginTop: "16px", marginBottom: "16px" }}
        />
        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: What makes it "Fair"?</strong>
            <br />
            A: Everyone gets the same price. No tiers or presale price
            advantages.
          </li>
          <li>
            <strong>Q: Why is minimum liquidity 51%?</strong>
            <br />
            A: To ensure a healthy trading pool and investor confidence.
          </li>
        </ul>

        <h4>Troubleshooting</h4>
        <ul>
          <li>
            <strong>Approve fails?</strong> Check gas balance and wallet
            connection.
          </li>
          <li>
            <strong>Logo invalid?</strong> Ensure URL ends in an image extension
            (.png, .jpg).
          </li>
        </ul>
      </>
    ),
  },
  finalize_fair_launch: {
    title: "Finalize a Fair Launch",
    content: (
      <>
        <h3>Finalizing a Fair Launch</h3>
        <h4>Overview</h4>
        <p>
          Finalizing triggers token distribution and liquidity creation. If your
          token has taxes/fees, you <strong>MUST exclude</strong> the presale
          address first.
        </p>

        <h4>Step 1: Exclude Fees (If Applicable)</h4>
        <p>
          <em>Only if your token has transaction fees/taxes.</em>
        </p>
        <ol>
          <li>
            Go to your pool page and copy the <strong>Presale Address</strong>.
          </li>
          <li>Go to your token contract on the block explorer.</li>
          <li>Connect owner wallet to "Write Contract".</li>
          <li>
            Find <code>excludeFromFees</code> (or similar) and input the Presale
            Address. Write transaction.
          </li>
        </ol>

        <h4>Step 2: Finalize Pool</h4>
        <ol>
          <li>
            Go to the <strong>Pool Actions</strong> section on your TrendPad
            pool page.
          </li>
          <li>
            Click <strong>Finalize</strong> (active if Soft Cap met & Ended).
          </li>
          <img
            src="/docs/presale/16.png"
            alt="Set claim time"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
          <li>
            Click the finalize button for Confirming the transaction. This will
            list the token on the DEX and lock liquidity.
          </li>
          <img
            src="/docs/presale/17.png"
            alt="Finalize confirmation"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />{" "}
        </ol>

        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: What does Finalize do?</strong>
            <br />
            A: Calculates price, distributes tokens, adds liquidity to DEX, and
            sends raised funds to you.
          </li>
          <li>
            <strong>Q: Why exclude fees?</strong>
            <br />
            A: To prevent the contract's bulk transfers from being taxed, which
            causes failure.
          </li>
        </ul>

        <h4>Troubleshooting</h4>
        <ul>
          <li>
            <strong>Transaction fails?</strong> Check if you excluded fees
            correctly. Check gas.
          </li>
          <li>
            <strong>Button greyed out?</strong> Ensure pool has ended and Soft
            Cap met.
          </li>
        </ul>
      </>
    ),
  },
  fair_launch_buy_back: {
    title: "Fair Launch Buy-Back Option",
    content: (
      <>
        <h3>Fair Launch Buy-Back</h3>
        <h4>Overview</h4>
        <p>
          An optional feature that uses a portion of raised funds to
          automatically buy back and burn tokens, supporting the price.
        </p>

        <h4>Setup</h4>
        <p>Enable "Buyback" in Step 2 of creation.</p>
        <ul>
          <img
            src="/docs/fairlaunch/6.png"
            alt="Buyback Setup"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
          <li>
            <strong>Buyback %:</strong> Funds allocated for buybacks.
          </li>
          <li>
            <strong>Constraint:</strong> (Buyback % + Liquidity %) MUST be &ge;
            51%.
          </li>
          <img
            src="/docs/fairlaunch/7.png"
            alt="Buyback Triggers"
            style={{
              maxWidth: "100%",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
          <li>
            <strong>Triggers:</strong> Set limits on how often buybacks can be
            triggered (Min/Max Delay).
          </li>
        </ul>

        <h4>How it Works</h4>
        <p>
          Once finalized, anyone (subject to delays) can click the Buy-Back
          button on the pool page to trigger a buy-and-burn transaction.
        </p>

        <h4>Q&A</h4>
        <ul>
          <li>
            <strong>Q: Is it mandatory?</strong>
            <br />
            A: No, it is optional.
          </li>
          <li>
            <strong>Q: Who pays gas?</strong>
            <br />
            A: The user who clicks the trigger button pays the gas.
          </li>
        </ul>
      </>
    ),
  },
};
